import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NewsCard, PageHero } from "@/components/public/Shared";

const CATS = ["Semua", "Berita", "Kegiatan", "Pengumuman", "Pengawasan", "Kelembagaan", "Regulasi", "Informasi Publik", "Edukasi"];

export default function InfoBawaslu() {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get("search") || "");
  const [category, setCategory] = useState("Semua");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 9 });
    if (search) p.set("search", search);
    if (category !== "Semua") p.set("category", category);
    api.get(`/news?${p}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [page, search, category]);

  useEffect(() => { load(); }, [load]);

  const onSearch = (e) => { e.preventDefault(); setPage(1); load(); };

  return (
    <div>
      <PageHero eyebrow="Informasi Publik" title="Info Bawaslu"
        description="Berita, kegiatan, pengumuman, dan informasi pengawasan Bawaslu Provinsi Sulawesi Tenggara." />

      <div className="container-page py-10">
        <div className="flex flex-col md:flex-row gap-3 mb-8">
          <form onSubmit={onSearch} className="flex flex-1 gap-2">
            <div className="flex items-center flex-1 bg-white border border-slate-200 rounded-lg px-3">
              <Search className="h-4 w-4 text-slate-400" />
              <Input data-testid="info-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari berita atau informasi..." className="border-0 shadow-none focus-visible:ring-0" />
            </div>
            <Button type="submit" className="bg-navy hover:bg-navy-700 text-white">Cari</Button>
          </form>
          <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
            <SelectTrigger className="w-full md:w-56" data-testid="info-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-400">Memuat berita…</div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-20 text-slate-400" data-testid="info-empty">Tidak ada berita ditemukan.</div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-5">Menampilkan {data.items.length} dari {data.total} informasi</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.items.map((n, i) => <NewsCard key={n.id} item={n} testid={`info-news-${i}`} />)}
            </div>
            {data.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10" data-testid="pagination">
                <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Sebelumnya</Button>
                {Array.from({ length: data.pages }).map((_, i) => (
                  <Button key={i} variant={page === i + 1 ? "default" : "outline"}
                    className={page === i + 1 ? "bg-navy text-white" : ""}
                    onClick={() => setPage(i + 1)}>{i + 1}</Button>
                ))}
                <Button variant="outline" disabled={page >= data.pages} onClick={() => setPage(page + 1)}>Selanjutnya</Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
